import React, { useEffect, useState } from 'react';
import api from '../../services/api';

interface SpellSettingsData {
  folder_id: string;
  model: string;
  api_key_mask: string;
  api_key_set: boolean;
  available_models: string[];
  service_enabled: boolean;
}

const SpellSettings: React.FC = () => {
  const [data, setData] = useState<SpellSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [folderId, setFolderId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('yandexgpt-lite');
  const [showApiKey, setShowApiKey] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/api/spell/settings');
      const d = resp.data as SpellSettingsData;
      setData(d);
      setFolderId(d.folder_id || '');
      setModel(d.model || 'yandexgpt-lite');
      setApiKey('');
    } catch (e: any) {
      setNotice({ type: 'error', text: e?.response?.data?.detail || 'Ошибка загрузки настроек' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const resp = await api.post('/api/spell/settings', {
        folder_id: folderId.trim(),
        api_key: apiKey.trim(),
        model: model.trim() || 'yandexgpt-lite',
      });
      setData((prev) => ({
        folder_id: resp.data.folder_id,
        model: resp.data.model,
        api_key_mask: resp.data.api_key_mask,
        api_key_set: !!resp.data.api_key_mask,
        available_models: prev?.available_models || ['yandexgpt-lite', 'yandexgpt', 'yandexgpt/rc'],
        service_enabled: resp.data.service_enabled,
      }));
      setApiKey('');
      setNotice({ type: 'success', text: 'Настройки сохранены и применены без перезапуска' });
    } catch (e: any) {
      setNotice({ type: 'error', text: e?.response?.data?.detail || 'Ошибка сохранения' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setNotice(null);
    try {
      const resp = await api.post('/api/spell/settings/test');
      if (resp.data?.ok) {
        setNotice({
          type: 'success',
          text: `YandexGPT (${resp.data.model}) работает. Пример: "${resp.data.sample_input}" → "${resp.data.sample_output}"`,
        });
      } else {
        setNotice({ type: 'error', text: resp.data?.error || 'Тест не пройден' });
      }
    } catch (e: any) {
      setNotice({ type: 'error', text: e?.response?.data?.detail || 'Ошибка теста' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="spell-settings-card">
        <div style={{ padding: 24, color: '#6b7280' }}>Загрузка настроек...</div>
      </div>
    );
  }

  return (
    <div className="spell-settings-card">
      <div className="spell-settings-header">
        <div className="spell-settings-title">
          <i className="bi bi-spellcheck" style={{ color: '#fc3f1d', marginRight: 8, fontSize: '1.25rem' }}></i>
          <span>Настройки YandexGPT (орфография)</span>
        </div>
        <div className={`spell-settings-status ${data?.service_enabled ? 'on' : 'off'}`}>
          <i className={`bi bi-${data?.service_enabled ? 'check-circle-fill' : 'exclamation-triangle-fill'}`}></i>
          {data?.service_enabled ? 'Сервис активен' : 'Сервис отключён'}
        </div>
      </div>

      {notice && (
        <div className={`spell-settings-notice ${notice.type}`}>
          <i className={`bi bi-${notice.type === 'success' ? 'check-circle' : notice.type === 'error' ? 'exclamation-triangle' : 'info-circle'}`}></i>
          {notice.text}
        </div>
      )}

      <div className="spell-settings-body">
        <div className="spell-settings-row">
          <label>Folder ID *</label>
          <input
            type="text"
            placeholder="b1g..."
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          />
          <small>Идентификатор каталога Yandex Cloud, где включён YandexGPT</small>
        </div>

        <div className="spell-settings-row">
          <label>API Key *</label>
          <div className="spell-settings-input-group">
            <input
              type={showApiKey ? 'text' : 'password'}
              placeholder={data?.api_key_set ? `Текущий: ${data.api_key_mask}  (оставьте пустым чтобы не менять)` : 'AQVN...'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <button type="button" onClick={() => setShowApiKey((s) => !s)} title={showApiKey ? 'Скрыть' : 'Показать'}>
              <i className={`bi bi-eye${showApiKey ? '-slash' : ''}`}></i>
            </button>
          </div>
          <small>
            API-ключ из раздела «Сервисные аккаунты» в консоли Yandex Cloud.
            {data?.api_key_set && ' Текущий ключ установлен, оставьте поле пустым чтобы не менять.'}
          </small>
        </div>

        <div className="spell-settings-row">
          <label>Модель</label>
          <select value={model} onChange={(e) => setModel(e.target.value)}>
            {(data?.available_models || ['yandexgpt-lite', 'yandexgpt', 'yandexgpt/rc']).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <small>
            <code>yandexgpt-lite</code> — дешевле и быстрее. <code>yandexgpt</code> — точнее на сложных текстах. <code>yandexgpt/rc</code> — preview новых версий.
          </small>
        </div>

        <div className="spell-settings-hint">
          <i className="bi bi-info-circle"></i>
          <div>
            <strong>Как получить ключи:</strong>
            <ol>
              <li>Зайти в <a href="https://console.cloud.yandex.ru/" target="_blank" rel="noreferrer">консоль Yandex Cloud</a></li>
              <li>Создать каталог или открыть существующий — скопировать <code>folder_id</code> из URL/боковой панели</li>
              <li>В каталоге → «Сервисные аккаунты» → создать аккаунт с ролью <code>ai.languageModels.user</code></li>
              <li>У созданного аккаунта → «Создать новый ключ» → выбрать «API-ключ» → скопировать значение (видно только один раз)</li>
              <li>Вставить значения сюда → «Сохранить»</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="spell-settings-footer">
        <button className="btn btn-secondary" onClick={handleTest} disabled={testing || !data?.service_enabled} title="Проверить ключи запросом к YandexGPT">
          {testing ? (<><i className="bi bi-hourglass-split"></i> Проверка...</>) : (<><i className="bi bi-lightning"></i> Тест</>)}
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} title="Сохранить и применить">
          {saving ? (<><i className="bi bi-hourglass-split"></i> Сохранение...</>) : (<><i className="bi bi-check-lg"></i> Сохранить</>)}
        </button>
      </div>
    </div>
  );
};

export default SpellSettings;
